import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';
import { createPortal } from 'react-dom';

type ToastContextType = (message: string, severity?: AlertColor) => void;

const ToastContext = createContext<ToastContextType>(() => {});

/**
 * Returns the shared toast notification dispatcher.
 *
 * @returns Function for showing a toast message.
 */
export const useToast = () => useContext(ToastContext);

/**
 * Provides shared toast notification state and rendering.
 *
 * @param props - Provider props.
 * @param props.children - React children rendered within the provider.
 * @returns Provider with global toast rendering.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [key, setKey] = useState(0);

  const showToast = useCallback(
    (message: string, severity: AlertColor = 'success') => {
      setMessage(message);
      setSeverity(severity);
      setKey(prev => prev + 1);
      setOpen(true);
    },
    []
  );

  const handleClose = () => setOpen(false);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {open &&
        createPortal(
          <Snackbar
            key={key}
            className="reusable-toast-snackbar"
            open={open}
            autoHideDuration={3000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              className="reusable-toast-alert"
              onClose={handleClose}
              severity={severity}
            >
              {message}
            </Alert>
          </Snackbar>,
          document.getElementById('toast-provider')
        )}
    </ToastContext.Provider>
  );
};
